import { kebabCase, toLower } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AdvancedDataGrid,
  Avatar,
  createInfoCardColumnDefinition,
  DataGridRef,
  FabButtonContainer,
  getOrganizationNameFromCard,
  INPUT_COLUMN_WIDTH,
  MembershipStatus,
  PRIMARY_COLUMN_MAX_WIDTH,
  PRIMARY_COLUMN_MIN_WIDTH,
  ScrollView,
  SelectedFilters,
  useFlags,
} from "@inductiveautomation/ws-components";

import { useNavigate } from "react-router";
import AccountAppBar from "../../components/AccountAppBar/AccountAppBar";
import { AccountFlags } from "../../featureFlags";
import { useGetAccountMembershipStructureQuery } from "./api/organizations.api";
import OrganizationsDialog from "./Organizations.Dialog";
import OrganizationsLeaveDialog from "./Organizations.LeaveDialog";
import OrganizationsDrawer from "./Organizations.Drawer";
import {
  getEmailsFromMemberships,
  getMembershipTypesFromMemberships,
  getStatusesFromMemberships,
  organizationToDataCard,
} from "./Organizations.data";
import {
  OrganizationActionType,
  OrganizationActionTypeForDialog,
} from "./Organizations.types";
import { getOrganizationStyles } from "./_organizations.styles";
import { useOrganizationActions } from "./hooks/useOrganizationActions";
import { useOrganizationQueries } from "./hooks/useOrganizationQueries";

const isDialogAction = (
  action?: OrganizationActionType
): action is OrganizationActionTypeForDialog =>
  (action && !(action === "accept_invite" || action === "launch_workspace")) ??
  false;

const statusesForFilters = (filters: SelectedFilters) => {
  const statusFilter = filters["filter-status"];
  if (!statusFilter) return [];

  return (statusFilter as string[]).map((listItemId) =>
    listItemId.replace("filter-status-", "")
  );
};

const membershipsForFilters = (filters: SelectedFilters) => {
  const membershipFilter = filters["filter-membership"];
  if (!membershipFilter) return [];

  return (membershipFilter as string[]).map((listItemId) =>
    listItemId.replace("filter-membership-", "")
  );
};

const emailsForFilters = (filters: SelectedFilters) => {
  const emailFilter = filters["filter-email"];
  if (!emailFilter) return [];

  return (emailFilter as string[]).map((listItemId) =>
    listItemId.replace("filter-email-", "")
  );
};

const emptyState = {
  organizationMemberships: [],
};

const Organizations = () => {
  const { t } = useTranslation();
  const dataGridRef = useRef<DataGridRef>(null);
  const { classes } = getOrganizationStyles();
  const { tsFeAccountOrgSectionVisibility } = useFlags<AccountFlags>();
  const navigate = useNavigate();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    if (tsFeAccountOrgSectionVisibility === false) {
      void navigate("/404");
    }
  }, [navigate, tsFeAccountOrgSectionVisibility]);

  const { useGetAccountOrganizationsQuery } = useOrganizationQueries();
  const { data = emptyState } = useGetAccountOrganizationsQuery();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[] | undefined>();
  const [currentAction, setCurrentAction] = useState<
    OrganizationActionType | undefined
  >();

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const selectedStatuses = statusesForFilters(selectedFilters);
  const selectedMemberships = membershipsForFilters(selectedFilters);
  const selectedEmails = emailsForFilters(selectedFilters);

  const { performAction } = useOrganizationActions();

  // For a single leave, fetch the org's structure so the dialog can show which child
  // orgs the user loses access to. Multi-org leave and other actions don't need it.
  const isSingleLeave = currentAction === "leave_organization";
  const leaveOrgId = isSingleLeave ? selectedIds?.[0] : undefined;
  // Use `currentData` instead of 'data': RTK Query keeps `data` pinned to the last
  // resolved result across arg changes, so switching from one org to another
  // would briefly expose the previous org's structure and flash the wrong
  // dialog. `currentData` is undefined while the new org's query is in flight,
  // which the `!leaveStructure` guard below relies on to defer rendering.
  const { currentData: leaveStructure } = useGetAccountMembershipStructureQuery(
    { orgId: leaveOrgId ?? "" },
    { skip: !leaveOrgId }
  );

  const onAction = async (
    actionId: OrganizationActionType,
    organizationIds: string[]
  ) => {
    if (isDialogAction(actionId)) {
      setSelectedIds(organizationIds);

      if (organizationIds.length > 1 && actionId === "leave_organization") {
        setCurrentAction("leave_multiple_organizations");
      } else {
        setCurrentAction(actionId);
      }
    } else {
      // This handles actions that _don't_ require confirmation in a dialog (only "accept_invite" and "launch_workspace").
      await performAction(actionId, organizationIds);

      setSelectedIds(undefined);
      dataGridRef.current?.clearCheckedAndSelected();
    }
  };

  const onSubmitDialog = () => {
    // This handles actions that require confirmation in a dialog (all but "accept_invite" and "launch_workspace").
    if (
      currentAction === "create_organization" ||
      currentAction === "create_organization_with_existing_organizations"
    ) {
      window.open(
        `${window._acctConfig?.WORKSPACE_URL}/onboarding?skipExistingCheck=true`
      );
    } else {
      void performAction(currentAction, selectedIds);
    }

    setCurrentAction(undefined);
    clearSelectedCards();
  };

  const statuses: MembershipStatus[] = getStatusesFromMemberships(
    data.organizationMemberships
  );
  const memberships = getMembershipTypesFromMemberships(
    data.organizationMemberships
  );
  const emails = getEmailsFromMemberships(data.organizationMemberships);

  const filteredSourceData = data.organizationMemberships.filter(
    (membership) => {
      const statusMatch =
        selectedStatuses.length === 0 ||
        selectedStatuses.includes(toLower(membership.membershipStatus));

      const nonMember = ["PENDING", "INVITED", "AVAILABLE"].includes(
        membership.membershipStatus
      );
      const membershipMatch =
        selectedMemberships.length === 0 ||
        (selectedMemberships.includes("none") && nonMember) ||
        (!nonMember &&
          selectedMemberships.includes(toLower(membership.membershipType)));

      const emailMatch =
        selectedEmails.length === 0 ||
        (membership.membershipEmail &&
          selectedEmails.includes(membership.membershipEmail));

      return statusMatch && membershipMatch && emailMatch;
    }
  );

  const cardMapper = organizationToDataCard(onAction);
  const gridDataCards = filteredSourceData.map(cardMapper);
  const selected = data.organizationMemberships.filter((membership) =>
    selectedIds?.includes(membership.organization.orgId)
  );

  const clearSelectedCards = () => {
    setSelectedIds(undefined);
    setDrawerOpen(false);
    dataGridRef.current?.clearCheckedAndSelected();
  };

  return (
    <div data-testid="organizations" className={classes.page}>
      <AccountAppBar
        title={[
          {
            label: "Organizations",
            id: "account-app-bar-organizations",
            onClick: () => undefined,
          },
        ]}
      />

      <ScrollView className={classes.scrollContainer}>
        <div className={classes.dataGridContainer}>
          <AdvancedDataGrid
            ref={dataGridRef}
            testId="organizations-data-grid"
            headings={[
              { id: "heading-name", label: t("organizations.headings.name") },
              {
                id: "heading-membership",
                label: t("organizations.headings.membership"),
              },
              { id: "heading-email", label: t("organizations.headings.email") },
              {
                id: "heading-status",
                label: t("organizations.headings.status"),
                alignment: "right",
              },
            ]}
            labels={{
              singular_item_name: t("organizations.singular_item_name"),
              plural_item_name: t("organizations.plural_item_name"),
            }}
            data={gridDataCards}
            generateCsvData={() => ({
              headings: [
                t("organizations.headings.name"),
                t("organizations.headings.membership"),
                t("organizations.headings.email"),
                t("organizations.headings.status"),
              ],
              rows: filteredSourceData.map((row) => [
                row.organization.orgName,
                row.organization.orgPath.join(" › "),
                row.organization.isVerified ? "Yes" : "No",
                row.membershipStatus,
              ]),
            })}
            onSelection={(rowId) => {
              if (rowId) {
                setSelectedIds([rowId]);
                setDrawerOpen(true);
              } else {
                clearSelectedCards();
              }
            }}
            selectedFilters={selectedFilters}
            onFiltersUpdated={(filters) => setSelectedFilters(filters)}
            filterChips={[
              {
                id: "filter-membership",
                label: t("organizations.data_grid.filters.membership"),
                variant: "list_advanced",
                listItems: [
                  [
                    ...memberships
                      .map((membership) => ({
                        id: `filter-membership-${toLower(membership)}`,
                        label: t(
                          `organizations.membership.${toLower(membership)}.label`
                        ),
                        variant: "checkbox" as const,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                    {
                      id: "filter-membership-none",
                      label: t(`organizations.membership.none.label`),
                      variant: "checkbox",
                    },
                  ],
                ],
              },
              {
                id: "filter-email",
                label: t("organizations.data_grid.filters.email"),
                variant: "list_advanced",
                listItems: [
                  emails
                    .map((email) => ({
                      id: `filter-email-${email}`,
                      label: email,
                      variant: "checkbox" as const,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ],
              },
              {
                id: "filter-status",
                label: "Status",
                variant: "list_advanced",
                listItems: [
                  statuses
                    .map((status) => ({
                      id: `filter-status-${toLower(status)}`,
                      label: t(`organizations.status.${toLower(status)}.label`),
                      variant: "checkbox" as const,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ],
              },
            ]}
            groupRowsBy={getOrganizationNameFromCard}
            chromeless
            customDummyCard={{
              id: `empty-${kebabCase(t("organizations.singular_item_name"))}-set`,
              disabled: true,
              hasDivider: false,
              contentColumns: [
                {
                  kind: "primary-with-avatar",
                  content: {
                    heading: `No ${t("organizations.plural_item_name").toLocaleLowerCase()}`,
                    avatar: (
                      <Avatar icon="organization" iconAvatarColor="neutral" />
                    ),
                    parentTestId: "organizations-data-grid",
                  },
                  minWidth: PRIMARY_COLUMN_MIN_WIDTH + INPUT_COLUMN_WIDTH,
                  maxWidth: PRIMARY_COLUMN_MAX_WIDTH + INPUT_COLUMN_WIDTH,
                },
                { content: null }, // membership
                { content: null }, // email
                createInfoCardColumnDefinition(null, 100, 160), // status
              ],
            }}
          />

          <OrganizationsDrawer
            selectedMembership={selected[0]}
            open={drawerOpen && selected.length === 1}
            onClose={() => {
              // We don't clear the selected IDs in the state hook here, as that means we have no IDs to pass to the `performAction` call.
              setDrawerOpen(false);
              dataGridRef.current?.clearCheckedAndSelected();
            }}
            onAction={onAction}
          />
        </div>
      </ScrollView>

      <FabButtonContainer
        icon="organization-add"
        label={t("organizations.actions.create_organization.button_label")}
        onClick={() => {
          if (data.organizationMemberships.length > 0) {
            setCurrentAction("create_organization_with_existing_organizations");
          } else {
            setCurrentAction("create_organization");
          }
        }}
      />

      {currentAction && isDialogAction(currentAction) ? (
        isSingleLeave && leaveStructure?.structure.children.length ? (
          // Org has children — show the tree dialog detailing affected access.
          <OrganizationsLeaveDialog
            testId="organizations-leave-dialog"
            organizationName={selected[0]?.organization.orgName ?? ""}
            structure={leaveStructure.structure}
            joinedOrgs={leaveStructure.joinedOrgs}
            onCancel={() => {
              setCurrentAction(undefined);
              clearSelectedCards();
            }}
            onSubmit={onSubmitDialog}
          />
        ) : isSingleLeave && !leaveStructure ? (
          // Still loading the structure — defer rendering to avoid showing the wrong dialog.
          null
        ) : (
          <OrganizationsDialog
            testId="organizations-dialog"
            selectedMemberships={selected}
            type={currentAction}
            onCancel={() => {
              setCurrentAction(undefined);
              clearSelectedCards();
            }}
            onSubmit={onSubmitDialog}
          />
        )
      ) : null}
    </div>
  );
};

export default Organizations;
